import React from 'react';
import Button from './Button';
import Modal from './Modal';
import { BoxData } from '../models/APIModels';
import { monthlyCostToMoney, purchaseCostToMoney } from '../utilities/MoneyConversion';

interface PreselectedBoxesModalProps {
  close: () => void;
  onSelectPredefinedBoxes: (preselectedBoxes: Record<number, number>) => void;
  /** The live catalogue, so a pack can show what is actually in it. */
  boxes: BoxData[];
}

interface Package {
  id: string;
  title: string;
  description: string;
  image: string;
  isMostPopular?: boolean;
  /** Product id → quantity. */
  contents: Record<number, number>;
}

// Contents are product ids from `/products/price`. Note id 11 is not in the
// catalogue the API serves — it resolves to nothing, here and in the page's
// own `applyBoxes`, so it is inert rather than harmful. Worth confirming with
// Ops what it was meant to be.
const PACKAGES: Package[] = [
  {
    id: 'small',
    title: 'Small room',
    description:
      'For students in a single room taking some of their belongings home.',
    image: '/img/room-small.png',
    contents: { 1: 1, 2: 1, 3: 1, 5: 1, 7: 1, 9: 2, 11: 1 },
  },
  {
    id: 'medium',
    title: 'Medium room',
    description: 'For university students heading back home.',
    image: '/img/room-medium.png',
    isMostPopular: true,
    contents: { 1: 1, 2: 2, 3: 2, 4: 2, 5: 3, 7: 1, 9: 3, 11: 2 },
  },
  {
    id: 'large',
    title: 'Large room',
    description:
      'For international students going home for the summer, or taking a year abroad.',
    image: '/img/room-large.png',
    contents: { 1: 1, 2: 4, 3: 3, 4: 2, 5: 6, 7: 2, 9: 5, 11: 2 },
  },
];

/** Resolves a pack's ids against the catalogue. Unknown ids drop out. */
function packContents(pack: Package, boxes: BoxData[]): BoxData[] {
  return boxes
    .filter((box) => (pack.contents[box.id] ?? 0) > 0)
    .map((box) => ({ ...box, count: pack.contents[box.id] }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * The suggested-packages picker.
 *
 * Two things were wrong beyond the styling. The card was a `<div onClick>`
 * whose action read "Select pack" in an `<h3>` — so a pack could not be
 * chosen by keyboard at all, and a screen reader met a heading where the
 * button was. And the packs were opaque: three names and a photo of a room,
 * with no way to know that "Medium room" means sixteen items and what they
 * cost, until you picked one and watched the catalogue behind fill in.
 * The contents and both prices are resolved from the live catalogue and shown
 * on the card.
 */
export function PreselectedBoxesModal({
  close,
  onSelectPredefinedBoxes,
  boxes,
}: PreselectedBoxesModalProps): JSX.Element {
  return (
    <Modal title="Suggested packages" close={close} wide>
      <p className="packages__lead">
        A ready-made starting point, built for UK student rooms. Pick one and
        you can still add or remove anything before you check out.
      </p>

      <ul className="packages">
        {PACKAGES.map((pack) => {
          const contents = packContents(pack, boxes);
          const itemCount = contents.reduce((sum, box) => sum + box.count, 0);

          return (
            <li
              className={`package${
                pack.isMostPopular ? ' package--popular' : ''
              }`}
              key={pack.id}
            >
              {pack.isMostPopular && (
                <span className="package__badge">Most popular</span>
              )}

              <div className="package__media">
                <img src={pack.image} alt="" />
              </div>

              <h2 className="package__title">{pack.title}</h2>
              <p className="package__description">{pack.description}</p>

              {contents.length > 0 && (
                <>
                  <ul
                    className="package__contents"
                    aria-label={`What is in the ${pack.title.toLowerCase()} pack`}
                  >
                    {contents.map((box) => (
                      <li key={box.id}>
                        <span className="package__contents-count">
                          {box.count}×
                        </span>{' '}
                        {box.name}
                      </li>
                    ))}
                  </ul>

                  {/*
                    The count sits with the two prices rather than heading the
                    list. It is the third figure you compare packs on, and on
                    a phone — where the itemised list stands down — carrying
                    it here is what keeps all three packs on one screen.
                  */}
                  <dl className="package__prices">
                    <div>
                      <dt>Items</dt>
                      <dd>{itemCount}</dd>
                    </div>
                    <div>
                      <dt>Materials</dt>
                      <dd>{purchaseCostToMoney(contents)}</dd>
                    </div>
                    <div>
                      <dt>Monthly</dt>
                      <dd>{monthlyCostToMoney(contents)}</dd>
                    </div>
                  </dl>
                </>
              )}

              {/*
                A real button, and the only interactive thing in the card. Its
                ::after covers the whole card so the card is still clickable,
                while the accessible name and the focus ring stay on the
                control — the card itself is not a tab stop, and there is no
                second, unnamed target for a screen reader to find.
              */}
              <Button
                variant="secondary"
                fullWidth
                className="package__select"
                onClick={() => onSelectPredefinedBoxes(pack.contents)}
              >
                Choose {pack.title.toLowerCase()}
              </Button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
